import { defineMessages, useIntl } from "react-intl";

import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import FaqAccordions from "@/patterns/wpl/faq-accordions/src";
import type { Feature } from "@/patterns/wpl/faq-accordions/src";

const LocalizedIntlLink = (chunks: React.ReactNode, href: string) => (
    <a href={useLocalizedHref(href)}>{chunks}</a>
);

const sharedRichtextValues = {
    ...DEFAULT_VALUES,
    p: (chunks: React.ReactNode) => <p>{chunks}</p>,
    strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
    ul: (chunks: React.ReactNode) => <ul>{chunks}</ul>,
    li: (chunks: React.ReactNode) => <li>{chunks}</li>,
    pricing: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/software/jira/service-management/pricing#"),
    standardCost: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "/licensing/jira-service-management#How-much-does-a-Jira-Service-Management-Cloud-Standard-subscription-cost"
        ),
    premiumCost: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "/licensing/jira-service-management#How-much-does-a-Jira-Service-Management-Cloud-Premium-subscription-cost"
        ),
    contact: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "/enterprise/contact?formType=pricing-quotes"
        ),
    refund: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "/licensing/cloud#what-is-atlassian-refund-policy"
        ),
    premium: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/software/jira/service-management/premium"),
    journeyToCloud: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/migration/assess/journey-to-cloud"),
    compareCloud: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "/migration/assess/compare-cloud-data-center"
        ),
    migrationAssessment: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/migration/assess/migration-assessment"),
    access: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/software/access"),
    atlassianGuardStandard: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/software/access"),
    atlassianGuardPremium: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/software/access"),
    licenseCloud: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/licensing/cloud"),
    licensingContact: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "/company/contact/purchasing-licensing#subscription"
        ),
    licensingLearnMore: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "https://confluence.atlassian.com/display/SERVICEDESKCLOUD/JIRA+applications+overview?_ga=2.130788171.596781427.1549903634-20577510.1548358088&amp;_gac=1.190216793.1547504438.Cj0KCQiAg_HhBRDNARIsAGHLV50SKrNohn3BM3kKhl5MlNmiknDrEN0mQhT1bC3LuffhLEnjtEgZRVYaAvoFEALw_wcB"
        ),
    downloadDataCenter: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "/software/jira/service-management/download/data-center"
        ),
    purchaseDataCenter: (chunks: React.ReactNode) =>
        LocalizedIntlLink(
            chunks,
            "/purchase/product/jira-servicedesk.data-center"
        ),
    licensingDataCenter: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/licensing/data-center"),
    licensingCommunity: (chunks: React.ReactNode) =>
        LocalizedIntlLink(chunks, "/software/views/community-license-request")
};

export const CloudPricingFaq = () => {
    const intl = useIntl();

    const messages = defineMessages({
        faqHeader: {
            id: "JiraServiceManagementPricingPage.cloud.faqHeader",
            defaultMessage: "Get more details"
        },
        calculateCostHeader: {
            id: "JiraServiceManagementPricingPage.cloud.calculateCostHeader",
            defaultMessage: "How to calculate your subscription cost"
        },
        calculateCostContent: {
            id: "JiraServiceManagementPricingPage.cloud.calculateCostContent",
            defaultMessage:
                "<p>Use our <pricing>pricing calculator</pricing> at the top of this page to determine the price of your subscription. Simply enter how many users you have and choose a billing cycle (annual or monthly).</p><p>Our detailed cloud pricing tables include price ranges for our <standardCost>Standard</standardCost> or <premiumCost>Premium</premiumCost> plans on our purchasing and licensing site. <contact>Contact our sales team</contact> for Enterprise pricing, available only for annual subscriptions.</p>"
        },
        paymentHeader: {
            id: "JiraServiceManagementPricingPage.cloud.paymentHeader",
            defaultMessage: "Payment options"
        },
        paymentContent: {
            id: "JiraServiceManagementPricingPage.cloud.paymentContent",
            defaultMessage:
                "<p>You can pay <strong>monthly subscriptions</strong> by credit card (MasterCard, Visa, or American Express) or PayPal.</p> <p>For <strong>annual subscriptions</strong>, you can pay by credit card, bank transfer, or check.</p>"
        },
        freeTrialPeriodHeader: {
            id: "JiraServiceManagementPricingPage.cloud.freeTrialPeriodHeader",
            defaultMessage:
                "Free trial period, refunds, & subscription plan details"
        },
        freeTrialPeriodContent: {
            id: "JiraServiceManagementPricingPage.cloud.freeTrialPeriodContent",
            defaultMessage:
                "<p>Our <strong>Free plan</strong> lets you try Jira Service Management for up to three agents with 2GB of storage and Community Support.</p> <p>If you'd like to add more than three agents or get access to more support and storage, you can sign up for a <strong>7-day free trial</strong> of our Standard or Premium plan. If you’re on the Free plan and exceed the user limit, we’ll automatically upgrade you to a 7-day free trial of the Standard plan. Cancel any time, no payment information required.</p> <p>If seven days isn’t enough, <licensingContact>let us know.</licensingContact> We offer an extension to a maximum of 30 days for our free trials.</p> <p>We offer <strong>annual subscriptions</strong> for JSM. For these subscriptions, we’ll bill you for the tier most closely matching your user count. Depending on the number of users purchased, annual subscriptions may be a better value.</p> <p>To purchase JSM as an annual subscription, start a free 7-day trial. Then, follow <licenseCloud>these instructions.</licenseCloud></p> <p>If you're <strong>unsatisfied</strong> with any Atlassian Cloud product or service, we offer a refund. Monthly subscriptions are eligible for a refund within your first paid month and after your trial period ends. Annual subscriptions are eligible for a refund within 30 days of payment. <licenseCloud>See how</licenseCloud> to request a refund.</p>"
        },
        definitionsHeader: {
            id: "JiraServiceManagementPricingPage.cloud.definitionsHeader",
            defaultMessage: "Definitions of agent and customer"
        },
        definitionsContent: {
            id: "JiraServiceManagementPricingPage.cloud.definitionsContent",
            defaultMessage:
                "<p>In Jira Service Management, an <strong>agent</strong> is defined as a user who can work on tickets and communicate directly with your customers. Agents can:</p> <ul> <li>View the portal, queues, reports, and SLA metrics within a service project</li> <li>View, add, edit, and delete customer-facing and internal comments on issues</li> <li>Add customers to a service project</li> <li>View, create, and manage content in the knowledge base</li> <li>Manage customers and organizations</li> </ul> <p>Jira users who aren’t licensed as agents in JSM will still be able to perform some actions in a ticket, like:</p> <ul> <li>Helping diagnose issues through internal comments with agents</li> <li>Watching tickets and receive notifications for updates</li> </ul> <p>A <strong>customer</strong> is anyone who creates requests through the customer portal or by email. Customers can:</p> <ul> <li>Create, comment on, and track requests through the customer portal</li> <li>Create and comment on requests via email</li> <li>Add comments and attachment to requests</li> <li>Add other participants to their own requests</li> </ul>"
        },
        aboutLicensesAndBillingHeader: {
            id: "JiraServiceManagementPricingPage.cloud.aboutLicensesAndBillingHeader",
            defaultMessage: "About licenses and billing by user type"
        },
        aboutLicensesAndBillingContent: {
            id: "JiraServiceManagementPricingPage.cloud.aboutLicensesAndBillingContent",
            defaultMessage:
                "<p>For <strong>monthly</strong> subscriptions, we’ll charge you for the following month’s subscription based on the exact number of Jira Service Management users you have. Add and remove agents as your team changes.</p> <p>For<strong> annual</strong> subscriptions, we’ll charge you for the tier that most closely matches your user count. Depending on the number of users purchased, annual subscriptions may be a better value.</p> <p>See monthly and annual tiers for our <standardCost>Standard </standardCost>or <premiumCost>Premium</premiumCost> plan.</p> <p><strong>Customers are free </strong>and don’t require a JSM license. Anyone can create a service request, and you'll never be limited to how many customers can access JSM.</p> <p>You don’t need to have the same number of Jira Software and JSM licenses. You can select and pay for what you need with each product.</p> <p>Still have questions on how roles and licensing work? <licensingLearnMore>Learn more.</licensingLearnMore></p>"
        },
        cloudSelfManagedHeader: {
            id: "JiraServiceManagementPricingPage.cloud.cloudSelfManagedHeader",
            defaultMessage: "Cloud and self-managed options"
        },
        cloudSelfManagedContent: {
            id: "JiraServiceManagementPricingPage.cloud.cloudSelfManagedContent",
            defaultMessage:
                "<p><strong>Important notice:</strong> We've ended sales for new server licenses and will end support for server on Feb 15, 2024, PT. We’re continuing investment in Data Center with several key improvements. <journeyToCloud>Learn what this means for you.</journeyToCloud></p><p>Our <strong>cloud</strong> products provide a complete SaaS solution, ready-made for all Atlassian customers. With cloud, we do the heavy lifting for you by providing built-in platform security and compliance, quick and simple set up, and financially backed SLAs for uptime and performance. Cloud comes with the added benefit of continuous innovation so your end-users will always have the latest and greatest features and functionality.</p> <p>Our <strong>Data Center (or, “self-managed”)</strong> products enable you the flexibility to deploy on an infrastructure of your choice. You’ll have control over data management, security and compliance, when you upgrade, and how you manage uptime and performance.</p> <p><compareCloud>Learn more about the differences</compareCloud>, or <migrationAssessment>take our assessment</migrationAssessment> to see which option best meets your organization’s needs.</p>"
        },
        samlSSOAndJSMHeader: {
            id: "JiraServiceManagementPricingPage.cloud.samlSSOAndJSMHeader",
            defaultMessage: "SAML SSO & JSM"
        },
        samlSSOAndJSMContent: {
            id: "JiraServiceManagementPricingPage.cloud.samlSSOAndJSMContent",
            defaultMessage:
                "<p>You can enable SAML single sign-on and other enhanced security features (like enforced 2-step verification, password policies, and user provisioning) with a subscription to <access>Atlassian Access</access>.</p> <p>Atlassian Access gives you one place to manage users and enforce security policies across all your Atlassian Cloud products including Jira, Confluence, and Bitbucket.</p>"
        },
        activeDirectoryHeader: {
            id: "JiraServiceManagementPricingPage.cloud.activeDirectoryHeader",
            defaultMessage: "Active Directory & JSM"
        },
        activeDirectoryContent: {
            id: "JiraServiceManagementPricingPage.cloud.activeDirectoryContent",
            defaultMessage:
                "<p>With a subscription to <access>Atlassian Access</access>, you can connect Jira Service Management directly to your Active Directory to automate the user provisioning process.</p>"
        },
        premiumOverStandardHeader: {
            id: "JiraServiceManagementPricingPage.cloud.premiumOverStandardHeader",
            defaultMessage: "Choosing Premium Plan over Standard"
        },
        premiumOverStandardContent: {
            id: "JiraServiceManagementPricingPage.cloud.premiumOverStandardContent",
            defaultMessage:
                "<p><premium>Jira Service Management Premium</premium> helps teams confidently scale their IT and operations by offering unlimited storage, a financially-backed 99.9% uptime SLA, advanced incident management capability, and 24/7 Premium Support with a 1-hour response time for critical issues.</p> <p>If you have concerns about downtime or service interruptions, are looking to scale JSM to support your org and customers, or have a globally distributed team that relies on Atlassian products as mission-critical for success, consider Premium.</p>"
        },
        atlassianGuardHeader: {
            id: "JiraServiceManagementPricingPageTeam24.moreDetails.atlassianGuard.title",
            defaultMessage: "Atlassian Guard and Premium plan"
        },
        atlassianGuardContent: {
            id: "JiraServiceManagementPricingPageTeam24.moreDetails.atlassianGuard.body",
            defaultMessage:
                "<p><premium>Jira Service Management Premium</premium> lets teams confidently scale with automation, a 99.9% uptime SLA, Premium Support, asset and configuration management, AI-powered service and operations, a virtual agent, and more.</p> <p>For security-conscious organizations that need more visibility and flexible controls in Atlassian cloud, <atlassianGuardStandard>Atlassian Guard Standard</atlassianGuardStandard> helps secure and manage users. You can automate manual processes and introduce security guardrails to reduce risk and free up time.</p> <p>For highly complex or regulated organizations with needs that go beyond centralized visibility and proactive controls, <atlassianGuardPremium>Atlassian Guard Premium</atlassianGuardPremium> offers the granularity to classify your organization’s data, alerts you of anomalous activity, and helps you investigate potential threats across Atlassian cloud.</p> <p>When you layer an Atlassian Guard plan on top of your Jira Service Management Premium plan, you’ll get an enterprise-grade solution that lets you scale and secure Atlassian cloud content and users across your entire organization.</p>"
        },
        differencesHeader: {
            id: "JiraServiceManagementPricingPage.cloud.differencesHeader",
            defaultMessage: "Differences between Jira products"
        },
        differencesContent: {
            id: "JiraServiceManagementPricingPage.cloud.differencesContent",
            defaultMessage:
                "<p>Jira Service Management is Atlassian’s service management solution for all teams, giving them everything they need for:</p> <ul> <li>Service request management</li> <li>Incident management</li> <li>Problem management</li> <li>Change management</li> <li>Asset and service configuration management</li> <li>Knowledge management</li> <li>Enterprise Service Management (ESM)</li> </ul> <p><strong>Jira Software</strong> is built specifically for software teams. Jira Software combines powerful developer tool integrations with the most important features and functionality required for great agile software development.</p> <p><strong>Jira Work Management</strong> is a simplified project management tool for customers looking to extend the power of Jira to their organization.</p>"
        }
    });

    const faqAccordionsFeatures: Feature[] = [
        {
            header: intl.formatMessage(messages.calculateCostHeader),
            content: intl.formatMessage(messages.calculateCostContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.paymentHeader),
            content: intl.formatMessage(messages.paymentContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.freeTrialPeriodHeader),
            content: intl.formatMessage(messages.freeTrialPeriodContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.definitionsHeader),
            content: intl.formatMessage(messages.definitionsContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.aboutLicensesAndBillingHeader),
            content: intl.formatMessage(
                messages.aboutLicensesAndBillingContent,
                {
                    ...sharedRichtextValues
                }
            )
        },
        {
            header: intl.formatMessage(messages.cloudSelfManagedHeader),
            content: intl.formatMessage(messages.cloudSelfManagedContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.samlSSOAndJSMHeader),
            content: intl.formatMessage(messages.samlSSOAndJSMContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.activeDirectoryHeader),
            content: intl.formatMessage(messages.activeDirectoryContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.premiumOverStandardHeader),
            content: intl.formatMessage(messages.premiumOverStandardContent, {
                ...sharedRichtextValues
            })
        },

        {
            header: intl.formatMessage(messages.atlassianGuardHeader),
            content: intl.formatMessage(messages.atlassianGuardContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.differencesHeader),
            content: intl.formatMessage(messages.differencesContent, {
                ...sharedRichtextValues
            })
        }
    ];

    return (
        <FaqAccordions
            header={intl.formatMessage(messages.faqHeader)}
            features={faqAccordionsFeatures}
        />
    );
};

export const DataCenterPricingFaq = () => {
    const intl = useIntl();
    const messages = {
        faqHeader: {
            id: "JiraServiceManagementPricingPage.datacenter.faqHeader",
            defaultMessage: "Get more details"
        },
        paymentHeader: {
            id: "JiraServiceManagementPricingPage.datacenter.paymentHeader",
            defaultMessage: "Payment options"
        },
        paymentContent: {
            id: "JiraServiceManagementPricingPage.datacenter.paymentContent",
            defaultMessage:
                "<p>You can pay for self-managed (also known as “Data Center”) licenses by credit card, bank transfer, check, or ACH (US banks only).</p> <p>Data Center is offered as an annual subscription that includes updates and support as long as your subscription is active.</p>"
        },
        freeTrialPeriodHeader: {
            id: "JiraServiceManagementPricingPage.datacenter.freeTrialHeader",
            defaultMessage: "Free trial & subscription info"
        },
        freeTrialPeriodContent: {
            id: "JiraServiceManagementPricingPage.datacenter.freeTrialContent",
            defaultMessage:
                "<p>You can host a fully functional copy of Jira Service Management on your own hardware free for 30 days. <downloadDataCenter>Download JSM</downloadDataCenter> for Data Center to get started.</p> <p>If you want to keep using JSM after your 30-day free trial, simply update your existing subscription with a purchased license key. If you need a license key, <purchaseDataCenter>purchase your subscription.</purchaseDataCenter></p> <p>If you're unsatisfied with any Atlassian product or service, we offer a 30–day money–back guarantee. <refund>See how</refund> to request a refund.</p> <p>Data Center is offered as an <strong>annual subscription</strong> that includes updates and support as long as your subscription is active.</p>"
        },
        definitionsHeader: {
            id: "JiraServiceManagementPricingPage.datacenter.definitionsHeader",
            defaultMessage: "Definitions of agent and customer"
        },
        definitionsContent: {
            id: "JiraServiceManagementPricingPage.datacenter.definitionsContent",
            defaultMessage:
                "<p>In Jira Service Management, an <strong>agent</strong> is defined as a user who can work on tickets and communicate directly with your customers. Agents can:</p> <ul> <li>View the portal, queues, reports, and SLA metrics within a service project</li> <li>View, add, edit, and delete customer-facing and internal comments on issues</li> <li>Add customers to a service project</li> <li>View, create, and manage content in the knowledge base</li> <li>Manage customers and organizations</li> </ul> <p>Jira users who aren’t licensed as agents in JSM will still be able to perform some actions in a ticket, like:</p> <ul> <li>Helping diagnose issues through internal comments with agents</li> <li>Watching tickets and receive notifications for updates</li> </ul> <p>A <strong>customer</strong> is anyone who creates requests through the customer portal or by email. Customers can:</p> <ul> <li>Create, comment on, and track requests through the customer portal</li> <li>Create and comment on requests via email</li> <li>Add comments and attachment to requests</li> <li>Add other participants to their own requests</li> </ul><p>Customers are free and don’t require a JSM license. Anyone can create a service request, and you'll never be limited to how many customers can access your Jira service desk.</p>"
        },
        discountsHeader: {
            id: "JiraServiceManagementPricingPage.datacenter.discountsHeader",
            defaultMessage: "About Data Center discounts"
        },
        discountsContent: {
            id: "JiraServiceManagementPricingPage.datacenter.discountsContent",
            defaultMessage:
                "<p>Depending on your organization’s situation, you may qualify for one of the following discounts:</p> <ul> <li><strong>Academic Data Center subscriptions: </strong>Qualified academic institutions receive a 50% discount on all Data Center subscriptions. <licensingDataCenter>Learn more.</licensingDataCenter></li> <li><strong>Community Data Center subscriptions:</strong> Qualified non-profit organizations receive free Data Center subscriptions (inclusive of both products and Marketplace apps). <licensingCommunity>Learn more.</licensingCommunity></li> </ul> "
        },
        cloudSelfManagedHeader: {
            id: "JiraServiceManagementPricingPage.datacenter.cloudSelfManagedHeader",
            defaultMessage: "About cloud and self-managed options"
        },
        cloudSelfManagedContent: {
            id: "JiraServiceManagementPricingPage.datacenter.cloudSelfManagedContent",
            defaultMessage:
                "<p><strong>Important notice:</strong> We've ended sales for new server licenses and will end support for server on Feb 15, 2024, PT. We’re continuing investment in Data Center with several key improvements. <journeyToCloud>Learn what this means for you.</journeyToCloud></p><p>Our <strong>cloud</strong> products provide a complete SaaS solution, ready-made for all Atlassian customers. With cloud, we do the heavy lifting for you by providing built-in platform security and compliance, quick and simple set up, and financially backed SLAs for uptime and performance. Cloud comes with the added benefit of continuous innovation so your end-users will always have the latest and greatest features and functionality.</p> <p>Our <strong>Data Center</strong> products enable you the flexibility to deploy on an infrastructure of your choice. You’ll have control over data management, security and compliance, when you upgrade, and how you manage uptime and performance.</p> <p><compareCloud>Learn more about the differences</compareCloud>, or <migrationAssessment>take our assessment</migrationAssessment> to see which option best meets your organization’s needs.</p>"
        },
        differencesHeader: {
            id: "JiraServiceManagementPricingPage.datacenter.differencesHeader",
            defaultMessage:
                "Differences between Jira Software and Jira Service Management"
        },
        differencesContent: {
            id: "JiraServiceManagementPricingPage.datacenter.differencesContent",
            defaultMessage:
                "<p><strong>Jira Software</strong> is a purpose built experience specifically for software teams. Jira Software combines powerful developer tool integrations with the most important elements of agile development.</p> <p><strong>Jira Service Management</strong> is purpose built for your IT and operations teams, giving them everything they need out of the box for incident, change, and problem management.</p>"
        },
        jsAndConfluenceHeader: {
            id: "JiraServiceManagementPricingPage.datacenter.jsAndConfluenceHeader",
            defaultMessage:
                "Jira Software and Confluence licenses for JSM users"
        },
        jsAndConfluenceContent: {
            id: "JiraServiceManagementPricingPage.datacenter.jsAndConfluenceContent",
            defaultMessage:
                "<p>Jira Service Management customers can browse <strong>Confluence</strong> knowledge base pages at no charge within the customer portal. Only knowledge base contributors, like agents, must be licensed in Confluence.</p> <p>You don’t need to have the same number of Jira Software and JSM licenses. Select and pay for what you need with each product.</p>"
        }
    };

    const faqAccordionsFeatures: Feature[] = [
        {
            header: intl.formatMessage(messages.paymentHeader),
            content: intl.formatMessage(messages.paymentContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.freeTrialPeriodHeader),
            content: intl.formatMessage(messages.freeTrialPeriodContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.definitionsHeader),
            content: intl.formatMessage(messages.definitionsContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.discountsHeader),
            content: intl.formatMessage(messages.discountsContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.cloudSelfManagedHeader),
            content: intl.formatMessage(messages.cloudSelfManagedContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.differencesHeader),
            content: intl.formatMessage(messages.differencesContent, {
                ...sharedRichtextValues
            })
        },
        {
            header: intl.formatMessage(messages.jsAndConfluenceHeader),
            content: intl.formatMessage(messages.jsAndConfluenceContent, {
                ...sharedRichtextValues
            })
        }
    ];

    return (
        <FaqAccordions
            header={intl.formatMessage(messages.faqHeader)}
            features={faqAccordionsFeatures}
        />
    );
};
